/**
 * Schedule row operations shared by Quick setup (block 5) and Availability & rules (6.D).
 *
 * `availability.schedules` keeps today's shape:
 *   [{ type:'event'|'event_staff', staffId, timezoneId, schedules:[{ dayId, dated:null|'DD-MM-YYYY', schedules:[{start_time,end_time}] }] }]
 * Every write goes through `update(STEPS.AVAILABILITY, …)` so the autosave engine sees it.
 */

import { useCallback } from "react";
import { useBuilder } from "../../state/builderContext";
import {
  DEFAULT_WORK_DAYS,
  DEFAULT_WORK_END,
  DEFAULT_WORK_START,
  SCHEDULE_SOURCE,
  SCHEDULE_TYPE,
  STEPS,
} from "../../state/constants";
import { defaultStaffTemplate, templateToRows } from "../../state/hydrate";
import * as api from "../../state/api";

export const defaultWeeklyRows = () =>
  DEFAULT_WORK_DAYS.map((dayId) => ({ dayId, dated: null, schedules: [{ start_time: DEFAULT_WORK_START, end_time: DEFAULT_WORK_END }] }));

export const eventRowOf = (schedules) => (schedules || []).find((r) => r.type === SCHEDULE_TYPE.EVENT) || null;
export const staffRowOf = (schedules, staffId) =>
  (schedules || []).find((r) => r.type === SCHEDULE_TYPE.EVENT_STAFF && parseInt(r.staffId, 10) === parseInt(staffId, 10)) || null;

// ---- pure row edits (ScheduleListView semantics) -------------------------------------------------------------

export const toggleDayRows = (rows, dayId, on) => {
  const others = (rows || []).filter((r) => r.dated || r.dayId !== dayId);
  if (!on) return others;
  if ((rows || []).some((r) => !r.dated && r.dayId === dayId)) return rows;
  // today's handleChange pushes an empty interval; we seed the default window so the row is valid at once
  return [...others, { dayId, dated: null, schedules: [{ start_time: DEFAULT_WORK_START, end_time: DEFAULT_WORK_END }] }];
};

const matchRow = (row, key) => (key.dated ? row.dated === key.dated : !row.dated && row.dayId === key.dayId);

export const addIntervalRows = (rows, key) =>
  // today's addSlotToDaySchedule pushes an empty interval; validation asks for both ends
  (rows || []).map((r) => (matchRow(r, key) ? { ...r, schedules: [...(r.schedules || []), { start_time: "", end_time: "" }] } : r));

export const removeIntervalRows = (rows, key, idx) =>
  (rows || []).map((r) => (matchRow(r, key) ? { ...r, schedules: (r.schedules || []).filter((_, i) => i !== idx) } : r));

export const setIntervalRows = (rows, key, idx, patch) =>
  (rows || []).map((r) =>
    matchRow(r, key) ? { ...r, schedules: (r.schedules || []).map((s, i) => (i === idx ? { ...s, ...patch } : s)) } : r
  );

/** Copy `fromDayId`'s intervals onto `toDayIds` (replacing theirs; other days and dated rows untouched). */
export const copyDayRows = (rows, fromDayId, toDayIds) => {
  const src = (rows || []).find((r) => !r.dated && r.dayId === fromDayId);
  if (!src) return rows;
  const copy = () => (src.schedules || []).map((s) => ({ ...s }));
  const targets = (toDayIds || []).filter((d) => d !== fromDayId);
  const kept = (rows || []).filter((r) => r.dated || !targets.includes(r.dayId));
  return [...kept, ...targets.map((dayId) => ({ dayId, dated: null, schedules: copy() }))];
};

/** Dated override: replace or add the `DD-MM-YYYY` row (DayScheduleSelectorModal.saveSchedule). */
export const setDatedRows = (rows, dated, intervals) => {
  const others = (rows || []).filter((r) => r.dated !== dated);
  return [...others, { dayId: null, dated, schedules: intervals.map((s) => ({ ...s })) }];
};
export const removeDatedRows = (rows, dated) => (rows || []).filter((r) => r.dated !== dated);

// ---- hook -----------------------------------------------------------------------------------------------------

export default function useScheduleOps() {
  const { state, update } = useBuilder();
  const { availability } = state;

  const setEventRow = useCallback(
    (rows, timezoneId, extra) => {
      const others = (availability.schedules || []).filter((r) => r.type !== SCHEDULE_TYPE.EVENT);
      update(STEPS.AVAILABILITY, {
        schedules: [{ type: SCHEDULE_TYPE.EVENT, staffId: 0, timezoneId: timezoneId || availability.timezone_id, schedules: rows }, ...others],
        ...(timezoneId ? { timezone_id: timezoneId } : {}),
        ...(extra || {}),
      });
    },
    [availability.schedules, availability.timezone_id, update]
  );

  /** Edit the event row's rows in place (list editor). Also switches the source to custom (D22). */
  const setEventRows = useCallback(
    (rows) => {
      const current = eventRowOf(availability.schedules);
      const others = (availability.schedules || []).filter((r) => r.type !== SCHEDULE_TYPE.EVENT);
      update(STEPS.AVAILABILITY, {
        schedules: [{ type: SCHEDULE_TYPE.EVENT, staffId: 0, timezoneId: (current && current.timezoneId) || availability.timezone_id, schedules: rows }, ...others],
        scheduleSource: SCHEDULE_SOURCE.CUSTOM,
        templateId: "",
      });
    },
    [availability.schedules, availability.timezone_id, update]
  );

  /** Copy a template's rows + timezone into the event (copy, not link — Event-Availbility-Component.getTemplateSchedule). */
  const applyTemplate = useCallback(
    async (templateId) => {
      if (!templateId) return false;
      try {
        const data = await api.getScheduleById(templateId);
        const { rows, timezoneId } = templateToRows(data);
        // New events inherit the account time zone; a template only supplies hours.
        setEventRow(rows, availability.timezone_id || timezoneId, { templateId: String(templateId), scheduleSource: SCHEDULE_SOURCE.TEMPLATE });
        return true;
      } catch (e) {
        return false;
      }
    },
    [setEventRow, availability.timezone_id]
  );

  /** `Custom availability` / `Set custom hours` / `Edit as custom hours`: keep the rows (D22). */
  const switchToCustomHours = useCallback(() => {
    if (!eventRowOf(availability.schedules)) {
      setEventRow(defaultWeeklyRows(), "", { scheduleSource: SCHEDULE_SOURCE.CUSTOM, templateId: "" });
      return;
    }
    update(STEPS.AVAILABILITY, { scheduleSource: SCHEDULE_SOURCE.CUSTOM, templateId: "" });
  }, [availability.schedules, setEventRow, update]);

  const setTimezone = useCallback(
    (timezoneId) => {
      const tz = String(timezoneId || "");
      update(STEPS.AVAILABILITY, {
        timezone_id: tz,
        schedules: (availability.schedules || []).map((r) => (r.type === SCHEDULE_TYPE.EVENT ? { ...r, timezoneId: tz } : r)),
      });
    },
    [availability.schedules, update]
  );

  // ---- per-staff rows (add-new-event-availbility.setStaffSchedule) ----------------------------------------------

  // These two read the step state at dispatch time (function patch): seeding is async (template
  // fetch), so two quick staff additions must not overwrite each other's rows.
  const setStaffRows = useCallback(
    (staffId, rows, timezoneId) => {
      const id = parseInt(staffId, 10);
      update(STEPS.AVAILABILITY, (current) => {
        const existing = staffRowOf(current.schedules, id);
        const others = (current.schedules || []).filter((r) => !(r.type === SCHEDULE_TYPE.EVENT_STAFF && parseInt(r.staffId, 10) === id));
        const row = {
          type: SCHEDULE_TYPE.EVENT_STAFF,
          staffId: id,
          timezoneId: timezoneId || (existing && existing.timezoneId) || current.timezone_id,
          schedules: rows,
        };
        return { schedules: [...others, row] };
      });
    },
    [update]
  );

  const removeStaffRow = useCallback(
    (staffId) => {
      const id = parseInt(staffId, 10);
      update(STEPS.AVAILABILITY, (current) => ({
        schedules: (current.schedules || []).filter((r) => !(r.type === SCHEDULE_TYPE.EVENT_STAFF && parseInt(r.staffId, 10) === id)),
      }));
    },
    [update]
  );

  /**
   * Seed a staff row the way today's `Event-Availbility-Component` does when a staff is selected:
   * from the staff's own `is_default` template — and from nothing else. Without such a template no
   * row is created, so booking keeps falling back to the staff's personal schedule, then the event's
   * (BookingTrait::getDateAvailability) exactly as it does today.
   *
   * `{ fallbackToEvent: true }` is only for the explicit "Custom hours for {staff}" action in
   * Availability & rules: the user asked for a staff row, so it starts from the event hours instead
   * of an empty list (today's panel starts empty and the user adds days).
   */
  const seedStaffRow = useCallback(
    async (staffId, { fallbackToEvent = false } = {}) => {
      if (staffRowOf(availability.schedules, staffId)) return;
      const def = defaultStaffTemplate(state.lookups.scheduleTemplates, staffId);
      if (def) {
        try {
          const data = await api.getScheduleById(def.value);
          const { rows, timezoneId } = templateToRows(data);
          setStaffRows(staffId, rows, timezoneId);
          return;
        } catch (e) {
          /* fall through */
        }
      }
      if (!fallbackToEvent) return;
      const eventRow = eventRowOf(availability.schedules);
      setStaffRows(staffId, eventRow ? eventRow.schedules.map((r) => ({ ...r, schedules: (r.schedules || []).map((s) => ({ ...s })) })) : defaultWeeklyRows(), "");
    },
    [availability.schedules, state.lookups.scheduleTemplates, setStaffRows]
  );

  const applyStaffTemplate = useCallback(
    async (staffId, templateId) => {
      if (!templateId) return false;
      try {
        const data = await api.getScheduleById(templateId);
        const { rows, timezoneId } = templateToRows(data);
        setStaffRows(staffId, rows, timezoneId);
        return true;
      } catch (e) {
        return false;
      }
    },
    [setStaffRows]
  );

  return {
    eventRow: eventRowOf(availability.schedules),
    setEventRow,
    setEventRows,
    applyTemplate,
    switchToCustomHours,
    setTimezone,
    setStaffRows,
    removeStaffRow,
    seedStaffRow,
    applyStaffTemplate,
  };
}
